import { Platform } from '@angular/cdk/platform';
import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient } from '@angular/common/http';
import localeHu from '@angular/common/locales/hu';
import { enableProdMode, importProvidersFrom, LOCALE_ID, provideAppInitializer } from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import { DateAdapter, MAT_DATE_LOCALE, MAT_DATE_FORMATS } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldDefaultOptions } from '@angular/material/form-field';
import { bootstrapApplication, BrowserModule } from '@angular/platform-browser';
import { provideRouter, Route, withComponentInputBinding } from '@angular/router';
import { provideStore } from '@ngrx/store';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { RECAPTCHA_V3_SITE_KEY } from 'ng-recaptcha-2';
import { LoggerModule, NgxLoggerLevel } from 'ngx-logger';

import { AppComponent } from './app/app.component';
import { AdminPrivilegeService } from './app/service/admin-privilege.service';
import { AdminRoleService } from './app/service/admin-role.service';
import { environment } from './environments/environment';

import { worker } from 'mocks/browser';
import { getAuthProviders, AUTH_CONFIG } from 'projects/admin/src/app/auth/auth-providers';
import getFormErrorMessage from 'projects/client/src/app/service/form-error-messages';
import { HunDateAdapter, DateFormats } from 'projects/client/src/app/utils/dateAdapter';
import { GET_FORM_ERROR_MESSAGE } from 'projects/design-system/components/form-error/form-error.component';
import { authReducer } from 'shared/auth/store/auth.reducer';
import { ENV_CONFIG } from 'shared/configs/environment.config';
import { HttpLoaderFactory } from 'shared/configs/translation.config';
import { APP_TYPE_CONFIG, AppTypeInterceptor } from 'shared/interceptors/app-type.interceptor';
import { HttpErrorInterceptor } from 'shared/interceptors/http-error.interceptor';
import { IncludeBearerTokenClassBasedInterceptor } from 'shared/interceptors/include-bearer-token-class-based-interceptor';
import { sharedProviders } from 'shared/modules/shared/shared.module';
import { ApplicationType } from 'shared/openapi/models';
import { PrivilegeService } from 'shared/services/privilege.service';
import { RoleService } from 'shared/services/role.service';

registerLocaleData(localeHu);

if (environment.production) {
	enableProdMode();
}

export const ROUTES: Route[] = [
	{
		path: '',
		loadChildren: () => import('./app/routing/routes'),
	},
];

bootstrapApplication(AppComponent, {
	providers: [
		provideAppInitializer(() => {
			const initializerFn = appInitializer();
			return initializerFn();
		}),
		provideRouter(ROUTES, withComponentInputBinding()),
		provideStore({
			auth: authReducer(ApplicationType.Admin),
		}),
		importProvidersFrom(
			BrowserModule,
			LoggerModule.forRoot({
				level: environment.production ? NgxLoggerLevel.OFF : NgxLoggerLevel.TRACE,
				serverLogLevel: NgxLoggerLevel.OFF,
				disableConsoleLogging: false,
			}),
			TranslateModule.forRoot({
				loader: {
					provide: TranslateLoader,
					useFactory: HttpLoaderFactory,
					deps: [HttpClient],
				},
			})
		),
		{
			provide: PrivilegeService,
			useClass: AdminPrivilegeService,
		},
		{
			provide: RoleService,
			useClass: AdminRoleService,
		},
		{
			provide: APP_TYPE_CONFIG,
			useValue: ApplicationType.Admin,
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: AppTypeInterceptor,
			multi: true,
			deps: [APP_TYPE_CONFIG],
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: HttpErrorInterceptor,
			multi: true,
		},
		{
			provide: GET_FORM_ERROR_MESSAGE,
			deps: [TranslateService],
			useFactory:
				(translateService: TranslateService) =>
				(errors: ValidationErrors | null | undefined): string =>
					getFormErrorMessage(errors, translateService),
		},
		{
			provide: ENV_CONFIG,
			useValue: environment,
		},
		sharedProviders,
		{
			provide: HTTP_INTERCEPTORS,
			useClass: IncludeBearerTokenClassBasedInterceptor,
			multi: true,
		},
		{
			provide: RECAPTCHA_V3_SITE_KEY,
			useValue: environment.captchaV3SiteKey,
		},
		{
			provide: AUTH_CONFIG,
			useValue: environment.auth,
		},
		getAuthProviders(environment.auth),
		{ provide: LOCALE_ID, useValue: 'hu-HU' },
		{
			provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
			useValue: <MatFormFieldDefaultOptions>{ appearance: 'outline' },
		},
		{ provide: DateAdapter, useClass: HunDateAdapter, deps: [MAT_DATE_LOCALE, Platform] },
		{ provide: MAT_DATE_LOCALE, useValue: 'hu-HU' },
		{ provide: MAT_DATE_FORMATS, useValue: DateFormats },
		{ provide: LOCALE_ID, useValue: 'hu-HU' },
	],
}).catch((err) => console.error(err));

declare global {
	interface Window {
		dataLayer: unknown[];
	}
}

export function appInitializer() {
	const promises: Promise<unknown>[] = [];

	if (environment.allowMSW) {
		const baseHref = document.getElementsByTagName('base')[0].href;
		const start = worker.start({
			serviceWorker: {
				url: baseHref + 'mockServiceWorker.js',
				options: {
					scope: baseHref,
				},
			},
		});

		promises.push(start);
	}

	return () => Promise.all(promises);
}
